import { useCallback, type ComponentProps } from 'react';
import { Link, useLocation, useNavigate, type To } from 'react-router-dom';
import { captureScrollBeforeNavigation } from '../hooks/useScrollRestoration';
import { getNavigationTransitionType, resolveTargetPath } from '../lib/navigation';
import { runViewTransition, supportsViewTransitions } from '../lib/viewTransition';

type TransitionLinkProps = ComponentProps<typeof Link>;

const TransitionLink = ({ onClick, to, state, ...props }: TransitionLinkProps) => {
  const location = useLocation();
  const navigate = useNavigate();

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      onClick?.(event);
      if (event.defaultPrevented) return;

      const targetPath = resolveTargetPath(to, location.pathname);
      if (targetPath === location.pathname) return;

      if (!supportsViewTransitions()) return;

      event.preventDefault();
      captureScrollBeforeNavigation(location.pathname);
      const type = getNavigationTransitionType(location.pathname, targetPath, 'PUSH');
      runViewTransition(() => navigate(to as To, { state }), type);
    },
    [location.pathname, navigate, onClick, state, to],
  );

  return <Link to={to} state={state} onClick={handleClick} {...props} />;
};

export default TransitionLink;
